/**
 * Can THIS browser actually render THIS output?
 *
 * Support differs by browser, by OS and by codec inside a container, and no static table stays
 * true: TIFF decodes only in Safari, HEIC only where the OS supplies the codec, AV1 plays
 * everywhere except Safari below an M3, HEVC depends on hardware in Chrome, Edge and Firefox.
 * A table alone will be wrong somewhere, so it is not the last word here — it decides whether
 * to *attempt* the real file, and an attempt that fails falls back to the server-rendered
 * poster. Being wrong optimistically therefore costs one request; being wrong pessimistically
 * costs a poster instead of the real thing. Both are recoverable, which is the point.
 *
 * Sources for the tables below (checked 2026-08-26):
 *   MDN, Image file type and format guide — https://developer.mozilla.org/en-US/docs/Web/Media/Guides/Formats/Image_types
 *   MDN, Web video codec guide — https://developer.mozilla.org/en-US/docs/Web/Media/Guides/Formats/Video_codecs
 *
 * See docs/proposed/RETENTION_FIXES.md T2.2/T2.3.
 */

export type CapabilityResult = 'yes' | 'no' | 'unknown';

/**
 * Decodable in Chrome, Edge, Firefox and Safari alike, so there is nothing to probe.
 *
 * BMP, ICO and APNG are on this list because they belong on it — an earlier version returned
 * `false` for anything without a canary, which quietly denied a preview to formats every
 * browser has decoded for years. WebP is universal on all four (Safari from 14 / Big Sur);
 * a Safari 13 user loses the inline preview and gets the poster, which is the safe direction.
 */
const UNIVERSAL_IMAGE = ['jpg', 'jpeg', 'jfif', 'png', 'apng', 'gif', 'bmp', 'ico', 'svg', 'webp'];

/** 1x1 payloads used to ask the decoder directly rather than guessing from the format name. */
const IMAGE_CANARIES: Record<string, string> = {
    // Minimal AVIF. Chrome 85+, Edge 121+, Firefox 93+, Safari 16.1+ — recent enough that
    // plenty of live browsers still say no, so it is worth asking.
    avif: 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=',
};

/**
 * Formats a browser may or may not decode, with no canary small enough to be worth inlining.
 * `ImageDecoder.isTypeSupported` answers for them where it exists (Chromium, and Safari 17+);
 * elsewhere the answer is "unknown", which means attempt-and-fall-back.
 */
const PROBEABLE_MIME: Record<string, string> = {
    heic: 'image/heic',
    heif: 'image/heif',
    tiff: 'image/tiff',
    tif: 'image/tiff',
    jxl: 'image/jxl',
};

const cache = new Map<string, Promise<CapabilityResult>>();

/**
 * Remember only settled answers.
 *
 * `'unknown'` is the absence of a determination, not one — caching it would freeze in an
 * "I couldn't tell" from the one moment the browser had no `ImageDecoder` yet, and we would
 * never ask again. Re-deriving it costs nothing.
 */
function remember(key: string, p: Promise<CapabilityResult>): Promise<CapabilityResult> {
    const tracked = p.then(result => {
        if (result === 'unknown') cache.delete(key);
        return result;
    });
    cache.set(key, tracked);
    return tracked;
}

function probeCanary(key: string, dataUri: string): Promise<CapabilityResult> {
    const hit = cache.get(key);
    if (hit) return hit;
    const p = new Promise<CapabilityResult>(resolve => {
        try {
            if (typeof Image === 'undefined') return resolve('unknown');
            const img = new Image();
            img.onload = () => resolve(img.width > 0 && img.height > 0 ? 'yes' : 'no');
            img.onerror = () => resolve('no');
            img.src = dataUri;
        } catch {
            resolve('unknown');
        }
    });
    return remember(key, p);
}

/**
 * Whether the browser can decode a still image format.
 *
 * Three answers, not two. `'unknown'` is the honest result for HEIC on a browser with no
 * `ImageDecoder`, and callers treat it as "worth attempting" — the failure is caught and shown
 * as a poster, so an attempt is cheap and a refusal is not free.
 */
export function canDecodeImage(format: string): Promise<CapabilityResult> {
    const f = (format || '').toLowerCase();
    if (UNIVERSAL_IMAGE.includes(f)) return Promise.resolve('yes');

    const canary = IMAGE_CANARIES[f];
    if (canary) return probeCanary(f, canary);

    const mime = PROBEABLE_MIME[f];
    if (mime) {
        const key = `decoder:${f}`;
        const hit = cache.get(key);
        if (hit) return hit;
        const p = (async (): Promise<CapabilityResult> => {
            try {
                const decoder = (globalThis as any).ImageDecoder;
                if (decoder && typeof decoder.isTypeSupported === 'function') {
                    return (await decoder.isTypeSupported(mime)) ? 'yes' : 'no';
                }
            } catch { /* fall through to unknown */ }
            return 'unknown';
        })();
        return remember(key, p);
    }

    // A format we have no opinion about. Attempting it is still better than refusing: the
    // caller falls back to the poster if the browser cannot render it.
    return Promise.resolve('unknown');
}

/**
 * Whether the browser claims it can play a container/codec pair.
 *
 * `'maybe'` means the container is recognised but the codec inside it may not be — which is
 * exactly the AV1-in-Safari and HEVC-without-hardware case. It maps to `'unknown'`, and callers
 * attempt it: `<video>` reports the truth by firing `error`, or by reaching metadata with
 * `videoWidth === 0`, and either lands the user on the poster.
 */
export function canPlay(mime: string): CapabilityResult {
    try {
        if (typeof MediaSource !== 'undefined' && typeof MediaSource.isTypeSupported === 'function') {
            if (MediaSource.isTypeSupported(mime)) return 'yes';
        }
        if (typeof document === 'undefined') return 'unknown';
        const el = document.createElement(mime.startsWith('audio/') ? 'audio' : 'video');
        const verdict = el.canPlayType(mime);
        return verdict === 'probably' ? 'yes' : verdict === 'maybe' ? 'unknown' : 'no';
    } catch {
        return 'unknown';
    }
}

/** Container + codec string for the media types we emit, when we know the codec. */
export function mimeFor(extension: string, codec?: string | null): string {
    const ext = (extension || '').toLowerCase();
    const c = (codec || '').toLowerCase();
    const container =
        ext === 'webm' ? 'video/webm'
            : ext === 'mp3' ? 'audio/mpeg'
                : ext === 'wav' ? 'audio/wav'
                    : ext === 'ogg' || ext === 'ogv' ? 'video/ogg'
                        : ext === 'oga' ? 'audio/ogg'
                            : ext === 'm4a' || ext === 'aac' ? 'audio/mp4'
                                : ext === 'flac' ? 'audio/flac'
                                    : ext === 'mov' ? 'video/quicktime'
                                        : ext === 'mkv' ? 'video/x-matroska'
                                            : 'video/mp4';
    if (!c) return container;
    // The codecs that actually decide playability for our outputs.
    const codecParam =
        c.includes('av1') || c.includes('av01') ? 'av01.0.05M.08'
            : c.includes('hevc') || c.includes('h265') || c.includes('h.265') ? 'hev1.1.6.L93.B0'
                : c.includes('vp9') ? 'vp9'
                    : c.includes('vp8') ? 'vp8'
                        : c.includes('h264') || c.includes('h.264') || c.includes('avc') ? 'avc1.42E01E'
                            : '';
    return codecParam ? `${container}; codecs="${codecParam}"` : container;
}

/**
 * Final answer for a converted output: is it worth trying to show inline?
 *
 * `'no'` means go straight to the poster and skip a download the browser cannot use.
 * `'unknown'` means attempt it — the caller recovers from a failed attempt, so a refusal here
 * would cost the user the real preview for no reason.
 */
export async function canPreviewOutput(extension: string, codec?: string | null): Promise<CapabilityResult> {
    const ext = (extension || '').toLowerCase();
    if (ext === 'pdf') return 'yes';
    if (UNIVERSAL_IMAGE.includes(ext) || IMAGE_CANARIES[ext] || PROBEABLE_MIME[ext]) {
        return canDecodeImage(ext);
    }
    // 'image' is the generic bucket some pages emit; we do not know the real extension, so
    // attempting is the only way to find out.
    if (ext === 'image') return 'unknown';
    return canPlay(mimeFor(ext, codec));
}
